import { createPortal } from "react-dom";
import { memo, useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { useGaiaMapContext } from "../hooks/useGaiaMapContext";
import { GAIA_DRAW_MODE } from "../GaiaMap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDrawPolygon,
    faLocationDot,
    faScribble,
    faArrows,
    faTrash,
    faMinus,
    faCheck,
    faXmark,
    faCopy,
    faCircle,
    faRuler,
    faObjectGroup,
    faCircleInfo,
    faPenToSquare,
    faTag,
    faClockRotateLeft,
} from "@fortawesome/pro-regular-svg-icons";

export type GaiaButton = {
    position: L.ControlPosition;
    show?: boolean;
    element: React.ReactNode;
};

interface GaiaMapControlsProps {
    map: L.Map | null;
    buttons?: GaiaButton[];
}

export const GaiaMapControls = memo(({ map, buttons }: GaiaMapControlsProps) => {
    const [containers, setContainers] = useState<Record<L.ControlPosition, HTMLDivElement>>({} as Record<L.ControlPosition, HTMLDivElement>);
    const mergedButtons = useMemo(() => buttons || [], [buttons]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const newContainers: Record<L.ControlPosition, HTMLDivElement> = {} as Record<L.ControlPosition, HTMLDivElement>;

        const positions = Array.from(new Set(mergedButtons.filter((b) => b.show).map((b) => b.position)));

        positions.forEach((position) => {
            const control = new L.Control({ position });
            control.onAdd = function () {
                const div = L.DomUtil.create("div", "leaflet-bar leaflet-control");
                newContainers[position] = div;
                return div;
            };
            control.addTo(map);
        });

        setContainers(newContainers);

        return () => {
            Object.values(newContainers).forEach((container) => {
                if (container.parentNode) {
                    container.parentNode.removeChild(container);
                }
            });
        };
    }, [map, mergedButtons]);

    return (
        <>
            {Object.entries(containers).map(([position, container]) => {
                const buttonsForThisPosition = mergedButtons.filter((button) => button.position === position && button.show);

                return createPortal(
                    <>
                        {buttonsForThisPosition.map((button, idx) => (
                            <div key={idx} style={{ marginBottom: "5px" }}>
                                {button.element}
                            </div>
                        ))}
                    </>,
                    container
                );
            })}
        </>
    );
});

const selectedDrawClass = "selected";
const DRAW_BUTTON_CLASS = "gaia-draw-button";

export const PointDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.MARKER ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.MARKER && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.MARKER);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faLocationDot} />
        </button>
    );
});

export const PolylineDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.POLYLINE ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.POLYLINE && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.POLYLINE);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faScribble} />
        </button>
    );
});

export const PolygonDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.POLYGON ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.POLYGON && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.POLYGON);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faDrawPolygon} />
        </button>
    );
});

export const SubtractDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.SUBTRACT ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.SUBTRACT && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.SUBTRACT);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faMinus} />
        </button>
    );
});

export const PanDrawButton = memo(() => {
    const { setDrawMode, drawMode, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === null ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (drawMode !== null && hasGeometry) {
                    cancelDrawing();
                }
                setDrawMode(null);
            }}
            className={classNames}
        >
            <FontAwesomeIcon icon={faArrows} />
        </button>
    );
});

export const DeleteDrawButton = memo(() => {
    const { readOnly, setDrawMode, drawMode, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.DELETE ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();

    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.DELETE && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.DELETE);
                }
            }}
            disabled={disabled}
            className={classNames}
            style={{ opacity: disabled ? 0.5 : 1 }}
        >
            <FontAwesomeIcon icon={faTrash} />
        </button>
    );
});

export const CloneDrawButton = memo(() => {
    const { onClone, drawMode, setDrawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.CLONE ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly || !onClone;

    if (!onClone) {
        return null;
    }

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.CLONE && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.CLONE);
                }
            }}
            disabled={disabled}
            className={classNames}
            style={{ height: "36px", width: "36px", padding: "8px" }}
        >
            <FontAwesomeIcon icon={faCopy} />
        </button>
    );
});

export const AcceptDrawingButton = memo(() => {
    const { drawMode, acceptDrawing, hasGeometry } = useGaiaMapContext();
    const isVisible = drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.RULER;
    if (!isVisible) return null;

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                acceptDrawing();
            }}
            className={DRAW_BUTTON_CLASS}
            title="Accept"
            disabled={drawMode === GAIA_DRAW_MODE.POLYGON && !hasGeometry}
        >
            <FontAwesomeIcon icon={faCheck} />
        </button>
    );
});

export const CancelDrawButton = memo(() => {
    const { drawMode, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const isDrawing = drawMode !== null;
    const classNames = `${DRAW_BUTTON_CLASS}`.trim();

    if (!isDrawing) {
        return null;
    }

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                cancelDrawing();
            }}
            disabled={!hasGeometry}
            className={classNames}
        >
            <FontAwesomeIcon icon={faXmark} />
        </button>
    );
});

export const CircleDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.CIRCLE ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.CIRCLE && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.CIRCLE);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faCircle} />
        </button>
    );
});

export const RulerDrawButton = memo(() => {
    const { setDrawMode, drawMode, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.RULER ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.RULER && hasGeometry) {
                    cancelDrawing();
                }
                setDrawMode(GAIA_DRAW_MODE.RULER);
            }}
            className={classNames}
            title="Ruler"
        >
            <FontAwesomeIcon icon={faRuler} />
        </button>
    );
});

export const SelectDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.SELECT ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.SELECT && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.SELECT);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faObjectGroup} />
        </button>
    );
});

export const DeselectDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.DESELECT ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.DESELECT && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.DESELECT);
                }
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faXmark} />
        </button>
    );
});

export const InspectDrawButton = memo(() => {
    const { setDrawMode, drawMode, cancelDrawing, hasGeometry, getFeatureInfo, wmsUrl } = useGaiaMapContext();

    if (!getFeatureInfo || !wmsUrl) {
        return null;
    }

    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.INSPECT ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = false;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.INSPECT && hasGeometry) {
                    cancelDrawing();
                }
                setDrawMode(GAIA_DRAW_MODE.INSPECT);
            }}
            disabled={disabled}
            className={classNames}
        >
            <FontAwesomeIcon icon={faCircleInfo} />
        </button>
    );
});

export const EditDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, cancelDrawing, hasGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.EDIT ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const disabled = readOnly || !hasGeometry;
    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.EDIT && hasGeometry) {
                        cancelDrawing();
                    }
                    setDrawMode(GAIA_DRAW_MODE.EDIT);
                }
            }}
            disabled={disabled}
            className={classNames}
            title="Edit Geometry"
        >
            <FontAwesomeIcon icon={faPenToSquare} />
        </button>
    );
});

export const LabelsDrawButton = memo(() => {
    const { setDrawMode, drawMode, cancelDrawing, hasGeometry, hasLabels } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.LABELS ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();

    if (!hasLabels) {
        return null;
    }

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (drawMode !== null && drawMode !== GAIA_DRAW_MODE.LABELS && hasGeometry) {
                    cancelDrawing();
                }
                setDrawMode(GAIA_DRAW_MODE.LABELS);
            }}
            className={classNames}
            title="Edit Label Positions"
        >
            <FontAwesomeIcon icon={faTag} />
        </button>
    );
});

export const ClonePreviousDrawButton = memo(() => {
    const { setDrawMode, drawMode, readOnly, onClonePreviousGeometry } = useGaiaMapContext();
    const selectedDrawStyle = drawMode === GAIA_DRAW_MODE.CLONE_PREVIOUS ? selectedDrawClass : "";
    const classNames = `${DRAW_BUTTON_CLASS} ${selectedDrawStyle}`.trim();
    const isDrawing = drawMode !== null && drawMode !== GAIA_DRAW_MODE.CLONE_PREVIOUS;
    const disabled = readOnly || !onClonePreviousGeometry || isDrawing;

    if (!onClonePreviousGeometry) {
        return null;
    }

    return (
        <button
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                    if (drawMode === GAIA_DRAW_MODE.CLONE_PREVIOUS) {
                        setDrawMode(null);
                    } else {
                        setDrawMode(GAIA_DRAW_MODE.CLONE_PREVIOUS);
                    }
                }
            }}
            disabled={disabled}
            className={classNames}
            title={isDrawing ? "Finish current drawing first" : "Clone Previous Geometry"}
            style={{ opacity: disabled ? 0.5 : 1 }}
        >
            <FontAwesomeIcon icon={faClockRotateLeft} />
        </button>
    );
});
